import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import React, { useEffect } from 'react'

import DownloadTopUpBreakdownButton from './DownloadTopUpBreakdownButton'
import { formatMoney, getLocaleDateString, handleStatusHover } from '../../utils'
import { TableSearchSingleColumn } from '../reusable/table/TableSearchSingleColumn'
import TanstackTable from '../reusable/table/TanstackTable'

export type PayoutLedgerEntryData = {
  id: string;
  source_type: string;
  amount: number;
  created_at: string;
  category: string;
  description: string;
}

const columnHelper = createColumnHelper<PayoutLedgerEntryData>()

const columns = [
  columnHelper.accessor('created_at', {
    header: 'Date',
    cell: (info) => getLocaleDateString(new Date(info.getValue())),
  }),
  columnHelper.accessor('amount', {
    header: 'Amount',
    cell: (info) => formatMoney(info.getValue()),
  }),
  columnHelper.accessor('category', {
    header: 'Category',
    cell: (info) => {
      let classes = ''
      let label = ''
      let statusTip = ''

      if (info.getValue() === 'automatic_monthly_topup') {
        classes = 'bg-green-100 text-green-800'
        label = 'Monthly referral fee'
      } else if (info.getValue() === 'payout') {
        classes = 'bg-blue-100 text-blue-600'
        label = 'Bank payout'
        statusTip = 'Bank payouts can take up to 5 business days to arrive in your account.'
      } else {
        classes = 'bg-gray-100 text-gray-800'
        label = 'Other'
      }

      return (
        <div
          onMouseEnter={(e) => statusTip && handleStatusHover(e, statusTip)}
          className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${classes}`}
        >
          {label}
        </div>
      )
    },
  }),
  columnHelper.accessor('description', {
    header: 'Description',
  }),
  columnHelper.accessor('id', {
    header: 'Details',
    cell: (info) =>
      info.row.original.source_type === 'Partners::ReferralFeeTopUp'
        ? (
          <div className='flex justify-center align-center'>
            <DownloadTopUpBreakdownButton ledgerId={info.getValue()} />
          </div>
          )
        : null
  }),
]

function PartnersPayoutsTable () {
  const [data, setData] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(false)

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  useEffect(() => {
    const fetchLedgerEntries = async () => {
      fetch('/api/broker/payouts', { // eslint-disable-line thatch/no-bare-fetch
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
        .then(async (response) => response.json())
        .then(async (json) => {
          setData(json.data.ledger_entries)
          table.setPageSize(20)
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false))
    }
    fetchLedgerEntries()
  }, [table])

  return (
    <div>
      <div className='flex items-center justify-between mb-3 w-full'>
        <div className='w-full sm:w-1/2 sm:max-w-sm'>
          <TableSearchSingleColumn table={table} columnId='description' placeholder='Search transaction descriptions' />
        </div>
      </div>
      <TanstackTable
        table={table}
        isLoading={loading}
        isError={error}
        paginationLabelSingular='transaction'
        noDataMessage={
          <div className='flex flex-col items-center justify-center gap-4'>
            You haven’t collected any referral fees yet. At the end of each month, we’ll credit your account for all your active clients.
          </div>
        }
      />
    </div>
  )
}

export default PartnersPayoutsTable
